// @ts-nocheck
import { useSearchParams } from 'react-router-dom';
import { ownerApi } from './ownerApi';
import { useLoad } from './lib/useLoad';
import { businessDisplayName, formatNumber, formatUsd } from './lib/format';
import { label } from './lib/labels';
import PageHeader from './ui/PageHeader';
import Card from './ui/Card';
import BusinessPicker from './ui/BusinessPicker';
import Alert from './ui/Alert';
import { SkeletonRows } from './ui/Skeleton';
import OwnerModelPrices from './OwnerModelPrices';

const AI_PATHS = ['luna', 'tira', 'owner_copilot'];

export default function OwnerCosts() {
  const [params, setParams] = useSearchParams();
  const tenant = params.get('tenant') || '';
  const period = params.get('period') || '';
  const filters = period ? { period } : {};
  const state = useLoad(() => (tenant ? ownerApi.tenantCosts(tenant, filters) : ownerApi.costs(filters)), [tenant, period]);
  const businesses = useLoad(() => ownerApi.subscribers().then((data) => data.subscribers || []), []);
  const data = state.data?.dashboard || state.data || {};
  const paths = data.ai_paths || {};
  return (
    <div className="space-y-6">
      <PageHeader
        title="AI costs"
        subtitle="What running the AI costs, per business. Prices are editable below and apply to the next message."
        actions={(
          <>
            <BusinessPicker
              businesses={businesses.data || []}
              value={tenant}
              onChange={(next) => setParams(next ? { tenant: next, period } : { period })}
            />
            <select aria-label="Period" value={period} onChange={(event) => setParams({ ...(tenant ? { tenant } : {}), ...(event.target.value ? { period: event.target.value } : {}) })} className="h-9 rounded-lg border border-[#7C8798] bg-white px-3 text-sm">
              {['', 'today', 'yesterday', 'last_7_days', 'last_30_days'].map((value) => <option key={value || 'all'} value={value}>{label('costPeriod', value)}</option>)}
            </select>
          </>
        )}
      />
      {tenant ? <p className="text-sm text-slate-700">Showing: {businessDisplayName((businesses.data || []).find((row) => row.tenant_id === tenant) || { tenant_id: tenant })}</p> : null}
      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}
      {state.status === 'error' ? <Alert title="We couldn't load costs." detail={state.error} /> : null}
      {state.status === 'ready' ? (
        <>
          <Card>
            <p className="text-sm text-slate-600">Confirmed cost (USD)</p>
            {data.known_usd == null ? <p className="mt-2 text-sm text-slate-700">No confirmed costs yet. Costs appear once AI providers&apos; prices are connected.</p> : <p className="text-3xl font-semibold">{formatUsd(data.known_usd)}</p>}
          </Card>
          <Card title="Luna, Tira, and Owner Copilot">
            {AI_PATHS.map((path) => {
              const row = paths[path] || {};
              const parts = [
                row.prompt_tokens ? `${formatNumber(row.prompt_tokens)} in` : '',
                row.cached_prompt_tokens ? `${formatNumber(row.cached_prompt_tokens)} cached` : '',
                row.completion_tokens ? `${formatNumber(row.completion_tokens)} out` : '',
                row.avg_latency_ms != null ? `${formatNumber(row.avg_latency_ms)} ms` : '',
              ].filter(Boolean);
              return (
                <p key={path} className="mt-2 text-sm">
                  {row.label || label('costPath', path)}
                  {' '}
                  <span className="tabular-nums">{formatUsd(row.cost_usd)}</span>
                  <span className="text-slate-600">
                    {' · '}
                    avg {formatUsd(row.avg_per_message_usd)} / message
                    {row.messages ? ` · ${formatNumber(row.messages)} messages` : ''}
                    {parts.length ? ` · ${parts.join(' · ')}` : ''}
                  </span>
                </p>
              );
            })}
          </Card>
          <Card title="Usage this period">
            {Object.entries(data.pending_by_category || {}).map(([key, count]) => (
              <p key={key} className="mt-2 text-sm">{label('costCategory', key)} <span className="tabular-nums">{formatNumber(count)}</span></p>
            ))}
          </Card>
          {data.attribution_note ? <p className="text-sm text-slate-600">{data.attribution_note}</p> : null}
          <OwnerModelPrices />
        </>
      ) : null}
    </div>
  );
}
